import * as React from "react";
import { styled } from "@linaria/react";
import { color, fonts } from "@ui";
import t from "../../../../../../i18n";
import Link from "@ui/Link";
import type { ErrorType, FileKeys, MapFile } from "./FilesType";
import { ErrorMessage, FileType } from "./FilesType";
import classNames from "classnames";
import useToasts from "@ui/Toast/useToasts";
import { css } from "@linaria/core";
import { ErrorWarningFill, FileCopyLine, InformationLine } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";

const Infos = styled.div`
  display: flex;
  padding: 0 4px;
  row-gap: 2px;
  flex-direction: column;
  align-items: flex-start;
  font-weight: 500;
`;

const GroupTitle = styled.div`
  display: flex;
  font: ${fonts.body2};
`;

const Info = styled.div`
  display: flex;
  &.error {
    /* flex-direction: column; */
    align-items: flex-start;
  }
  align-items: center;
  flex-wrap: wrap;
  font: ${fonts.body2};
`;

const ProblematicFileInfo = styled(Info)`
  margin-top: 15px;
  display: block;
`;

const Property = styled.span`
  display: inline-block;
  column-gap: 3px;
  white-space: nowrap;
  font-weight: 600;

  &::before {
    content: "-";
    padding-right: 2px;
  }
`;

const Value = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  white-space: normal;
  margin-left: 4px;
  column-gap: 4px;
`;

const duplicatedFileStyle = css`
  div + div::before {
    margin: -2px;
    content: ", ";
  }
`;

const StyledLink = styled(Link)`
  color: ${color.lightNitrogen500_Main};
  font-weight: 500;
  :hover {
    text-decoration: underline;
  }
  margin-left: 6px;
`;

const ValidityMessage = styled.div`
  margin-top: 14px;
  font: ${fonts.body2SemiBold};
  color: ${color.carbon900};
  &.warning {
    color: ${color.oxygen400};
  }
`;

const WarningTriangle = styled(ErrorWarningFill)`
  fill: ${color.error};
  height: 14px;
  width: 14px;
  margin-right: 2px;
`;

const StyledTooltip = styled(TooltipV2)`
  max-width: 350px;
`;

const CopyContainer = styled.div`
  display: flex;
  color: ${color.titanium600};
  font: ${fonts.body3SemiBold};
  cursor: pointer;
  gap: 2px;
`;

interface InfoTooltip {
  children: React.ReactNode;
  overlay: React.ReactNode;
}
const InfoTooltip = ({ children, overlay }: InfoTooltip) => (
  <StyledTooltip
    overlay={overlay}
    gap={4}
    closeDelay={200}
    placement="right"
    borderColor={undefined}
  >
    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>{children}</span>
  </StyledTooltip>
);
const noneValue = (status: ErrorType, overlay: string) => (
  <InfoTooltip overlay={overlay}>
    {t("commons.none_value", {
      icon:
        status === "error" ? (
          <WarningTriangle style={{ marginLeft: "2px" }} />
        ) : (
          <InformationLine color={color.carbon800} style={{ marginLeft: "2px" }} size="xs" />
        ),
    })}
  </InfoTooltip>
);

interface Binding {
  mapFile?: MapFile;
}

const BindingFileError = ({ mapFile }: Binding) => {
  const { addToast } = useToasts();
  const copy = React.useCallback(
    (mapFile: string, displayInfo = false) =>
      () => {
        void window.navigator.clipboard.writeText(mapFile);
        if (displayInfo) addToast("info", t("setup.paragraphs.model.error.copied"));
      },
    [addToast],
  );

  return (
    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
      <span>{mapFile?.fileName[0]}</span>
      <InfoTooltip
        overlay={
          <div>
            {mapFile?.error?.[0]?.message.includes(ErrorMessage.missingColumn)
              ? t("setup.simulation.modal.binding.error.missing_column")
              : null}
            {mapFile?.error?.[0]?.message.includes(ErrorMessage.unknownQuantity)
              ? t("setup.simulation.modal.binding.error.unknown_quantity", {
                  data: mapFile?.error?.[0]?.data ?? [],
                })
              : null}
            {mapFile?.error?.[0]?.message.includes(ErrorMessage.invalidExternalGap)
              ? t("setup.simulation.modal.binding.error.invalid_external_gap", {
                  data: mapFile?.error?.[0]?.data,
                })
              : null}
            {mapFile?.error?.[0]?.message.includes(ErrorMessage.invalidOrderExternalGap)
              ? t("setup.simulation.modal.binding.error.orderExternalGapValue", {
                  data: mapFile?.error?.[0]?.data,
                })
              : null}
            {mapFile?.error?.[0]?.message.includes(ErrorMessage.invalidReferenceValue)
              ? t("setup.simulation.modal.binding.error.referenceValue", {
                  data: mapFile?.error?.[0]?.data,
                })
              : null}
            {mapFile?.error?.[0]?.message.includes(
              ErrorMessage.invalidOrderExternalAscendingArrayStartingWithOne,
            )
              ? t("setup.simulation.modal.binding.error.order_external_no_ascending_starting_one")
              : null}
            {mapFile?.error?.[0]?.message === ErrorMessage.CannotDependOnPastFunction
              ? t("setup.simulation.input_model.error.has_time_function_dependency")
              : null}
          </div>
        }
      >
        <WarningTriangle />
      </InfoTooltip>
      <CopyContainer onClick={copy(JSON.stringify(mapFile), true)} role="button">
        <FileCopyLine size="s" /> {t("commons.copyDetail")}
      </CopyContainer>
    </div>
  );
};

type SectionType = FileKeys | "input" | "output";

interface M {
  modelType: string;
  submitted: boolean;
  validation?: Map<FileKeys, MapFile>;
  invalid: boolean;
  sectionsDisplayed?: SectionType[];
}

const ModalInfos = ({
  modelType,
  submitted,
  validation,
  invalid,
  sectionsDisplayed = ["main", "variables", "parameters", "image", "binding"],
}: M) => {
  const duplicatedFiles = [];

  duplicatedFiles.push(
    ...(sectionsDisplayed.find(s => s === "main")
      ? (validation?.get("main")?.fileName.slice(1) ?? [])
      : (validation?.get("main")?.fileName ?? [])),
  );

  duplicatedFiles.push(
    ...(sectionsDisplayed.find(s => s === "variables")
      ? (validation?.get("variables")?.fileName.slice(1) ?? [])
      : (validation?.get("variables")?.fileName ?? [])),
  );

  duplicatedFiles.push(
    ...(sectionsDisplayed.find(s => s === "parameters")
      ? (validation?.get("parameters")?.fileName.slice(1) ?? [])
      : (validation?.get("parameters")?.fileName ?? [])),
  );

  duplicatedFiles.push(
    ...(sectionsDisplayed.find(s => s === "binding")
      ? (validation?.get("binding")?.fileName.slice(1) ?? [])
      : (validation?.get("binding")?.fileName ?? [])),
  );

  duplicatedFiles.push(
    ...(sectionsDisplayed.find(s => s === "image")
      ? (validation?.get("image")?.fileName.slice(1) ?? [])
      : (validation?.get("image")?.fileName ?? [])),
  );

  return (
    <Infos>
      <GroupTitle>
        {submitted ? t("commons.modal.identified_files") : t("commons.modal.expected_file_format")}
      </GroupTitle>
      {/* main model */}
      {sectionsDisplayed.find(s => s === "main") && (
        <Info>
          <Property>
            {t("setup.simulation.modal.expected_file_format.model.file", modelType, 1)}
          </Property>
          <Value>
            {submitted
              ? (validation?.get("main")?.fileName[0] ??
                noneValue(
                  "error",
                  modelType === FileType.dymola
                    ? t("setup.simulation.modal.main.dymola.tooltip.error")
                    : t("setup.simulation.modal.main.fmu.tooltip.error"),
                ))
              : t("setup.simulation.modal.expected_file_format.model.file.value", modelType, 1)}
          </Value>
        </Info>
      )}
      {/* variables model */}
      {sectionsDisplayed.find(s => s === "variables") && (
        <Info>
          <Property>
            {t("setup.simulation.modal.expected_file_format.model.file", modelType, 2)}
          </Property>
          <Value>
            {submitted
              ? (validation?.get("variables")?.fileName[0] ??
                (modelType === FileType.dymola
                  ? noneValue("error", t("setup.simulation.modal.variables.dymola.tooltip.error"))
                  : noneValue("info", t("setup.simulation.modal.fmu.tooltip.info"))))
              : t("setup.simulation.modal.expected_file_format.model.file.value", modelType, 2)}
          </Value>
        </Info>
      )}
      {/* parameters model */}
      {sectionsDisplayed.find(s => s === "parameters") &&
        (modelType === FileType.fmuTf || modelType === FileType.fmuTo) && (
          <Info>
            <Property>
              {t("setup.simulation.modal.expected_file_format.model.file", modelType, 3)}
            </Property>
            <Value>
              {submitted
                ? (validation?.get("parameters")?.fileName[0] ??
                  noneValue("info", t("setup.simulation.modal.fmu.parameters.tooltip.info")))
                : t("setup.simulation.modal.expected_file_format.model.file.value", modelType, 3)}
            </Value>
          </Info>
        )}
      {/* image */}
      {sectionsDisplayed.find(s => s === "image") && (
        <Info>
          <Property>{t("setup.simulation.modal.expected_file_format.model_image")}</Property>
          <Value>
            {submitted
              ? (validation?.get("image")?.fileName[0] ??
                noneValue("info", t("setup.simulation.modal.image.tooltip.info")))
              : t("setup.simulation.modal.expected_file_format.model_image.value")}
          </Value>
        </Info>
      )}
      {/* binding inputs/outputs */}
      {sectionsDisplayed.find(s => s === "binding") && (
        <Info className={classNames({ error: validation?.get("binding")?.error })}>
          {sectionsDisplayed.find(s => s === "input") && (
            <Property>{t("setup.simulation.modal.expected_file_format.inputs")}</Property>
          )}
          {sectionsDisplayed.find(s => s === "output") && (
            <Property>{t("setup.simulation.modal.expected_file_format.outputs")}</Property>
          )}
          {!sectionsDisplayed.find(s => s === "input" || s === "output") && (
            <Property>{t("setup.simulation.modal.expected_file_format.inputs_outputs")}</Property>
          )}
          <Value>
            {submitted ? (
              validation?.get("binding") ? (
                validation?.get("binding")?.error ? (
                  <BindingFileError mapFile={validation.get("binding")} />
                ) : (
                  validation?.get("binding")?.fileName[0]
                )
              ) : (
                noneValue("info", t("setup.simulation.modal.binding.tooltip.info"))
              )
            ) : (
              t(
                "setup.simulation.modal.expected_file_format.inputs_outputs.value",
                sectionsDisplayed.find(s => s === "input" || s === "output") === undefined,
              )
            )}
          </Value>
          {!submitted && (
            <StyledLink download href="/metroscopia_example_binding.csv">
              {t("commons.modal.expected_file.link")}
            </StyledLink>
          )}
        </Info>
      )}

      {submitted &&
        (validation?.get("nonExpected")?.fileName[0] !== undefined ||
          validation?.get("main")?.fileName[1] ||
          validation?.get("variables")?.fileName[1] ||
          validation?.get("parameters")?.fileName[1] ||
          validation?.get("binding")?.fileName[1] ||
          validation?.get("image")?.fileName[1]) && (
          <ProblematicFileInfo>
            <GroupTitle>
              {t("setup.simulation.modal.expected_file_format.non_identified")}
            </GroupTitle>

            {/* non expected file */}
            {typeof validation.get("nonExpected")?.fileName[0] === "string" && (
              <Info>
                <Property>
                  {t("setup.simulation.modal.expected_file_format.unexpected_file")}
                </Property>
                <Value>{validation.get("nonExpected")?.fileName.join(", ")}</Value>
              </Info>
            )}

            {/* duplicated file */}
            {(validation?.get("main")?.fileName[1] ||
              validation?.get("variables")?.fileName[1] ||
              validation?.get("parameters")?.fileName[1] ||
              validation?.get("binding")?.fileName[1] ||
              validation?.get("image")?.fileName[1]) && (
              <Info>
                <Property>
                  {t("setup.simulation.modal.expected_file_format.already_existing")}
                </Property>
                <Value className={duplicatedFileStyle}>
                  {duplicatedFiles.map(file => (
                    <div key={file}>{file}</div>
                  ))}
                </Value>
              </Info>
            )}
          </ProblematicFileInfo>
        )}

      {/* not valid and confirm message */}
      {submitted && (
        <ValidityMessage className={classNames({ warning: invalid })}>
          {invalid
            ? t("commons.modal.not_valid_form.message", {
                icon: <WarningTriangle />,
              })
            : t("commons.modal.confirm_loading.message")}
        </ValidityMessage>
      )}
    </Infos>
  );
};

export default React.memo(ModalInfos);
